// src/components/reuseable/EditableNotes.jsx
// Always-editable Notes field for the buyer/listing lead drawers. Saves on blur
// via the provided onSave (which returns the lead-update result, e.g.
// { success, error }). Falls back to read-only text when the viewer can't manage
// the lead.
import { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";

export default function EditableNotes({
  value,
  onSave,
  canEdit = true,
  hideLabel = false,
}) {
  const [text, setText] = useState(value || "");
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const lastSaved = useRef(value || "");

  useEffect(() => {
    setText(value || "");
    lastSaved.current = value || "";
  }, [value]);

  if (!canEdit) {
    return value ? (
      <div className="py-2 border-b border-slate-100">
        {!hideLabel && <div className="text-slate-500 text-sm mb-1">Notes</div>}
        <div className="text-slate-800 text-sm whitespace-pre-wrap">{value}</div>
      </div>
    ) : null;
  }

  const save = async () => {
    if (text === lastSaved.current) return; // nothing changed
    setSaving(true);
    try {
      const res = await onSave(text);
      if (res && res.success === false) {
        throw new Error(res.error || "save failed");
      }
      lastSaved.current = text;
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 1500);
    } catch (e) {
      toast.error("Couldn't save the note — try again");
      setText(lastSaved.current); // revert to last good value
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="py-2">
      <div className="flex items-center justify-between mb-1">
        {hideLabel ? (
          <span />
        ) : (
          <span className="text-slate-500 text-sm">Notes</span>
        )}
        <span className="text-[11px] text-slate-400 h-4">
          {saving ? "Saving…" : justSaved ? "Saved ✓" : ""}
        </span>
      </div>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={save}
        rows={3}
        placeholder="Add a note…"
        className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800 outline-none focus:border-[#1E293B] resize-y"
      />
    </div>
  );
}
